import { useState } from 'react'
import {
  Wallet,
  TrendingUp,
  Target,
  PieChart,
  Plus,
  Send,
  RefreshCw,
  CreditCard
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBalance } from '../contexts/BalanceContext'
import QuickPayment from '../components/QuickPayment'
import { GeometricBackground } from '../components/layouts/GeometricBackground'

import HomeSkeleton from '../components/skeletons/HomeSkeleton'

function Home() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const {
    categories,
    transactions,
    loading,
    totalBalance,
    totalSpent,
    totalBudget,
    loadData
  } = useBalance()

  // UI state
  const [showQuickPayment, setShowQuickPayment] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)

  // Use only user's actual categories (no defaults)
  const availableCategories = categories || []

  // Show skeleton while loading
  if (loading && transactions.length === 0 && categories.length === 0) {
    return <HomeSkeleton />
  }

  const handleCategoryPayment = (category) => {
    setSelectedCategory(category)
    setShowQuickPayment(true)
  }

  const recentTransactions = transactions.slice(0, 3)
  const topCategories = categories
    .sort((a, b) => (b.current_balance || 0) - (a.current_balance || 0))
    .slice(0, 3)

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#030303]">
      <GeometricBackground variant="home" className="fixed inset-0" />

      <div className="relative z-10 p-4 lg:p-6 max-w-7xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2 break-words">
            Welcome, {user?.name || 'User'}!
          </h1>
          <p className="text-white/60 text-sm lg:text-base">
            Here's your financial overview for today
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-6 lg:mb-8">
          {/* Total Balance */}
          <div className="glass-card p-3 lg:p-6 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border-white/10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-2 lg:mb-0">
                <p className="text-white/60 text-xs lg:text-sm">Total Balance</p>
                <p className="text-lg lg:text-2xl font-bold text-white break-all">
                  ₹{totalBalance.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="bg-indigo-500/20 p-2 rounded-full">
                <Wallet className="h-5 w-5 lg:h-6 lg:w-6 text-indigo-400 self-end lg:self-auto" />
              </div>
            </div>
          </div>

          {/* Monthly Salary */}
          <div className="glass-card p-3 lg:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-2 lg:mb-0">
                <p className="text-xs lg:text-sm text-white/60">Monthly Salary</p>
                <p className="text-lg lg:text-2xl font-bold text-white break-all">
                  ₹{totalBudget.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="bg-emerald-500/20 p-2 rounded-full">
                <TrendingUp className="h-5 w-5 lg:h-6 lg:w-6 text-emerald-400 self-end lg:self-auto" />
              </div>
            </div>
          </div>

          {/* Total Spent */}
          <div className="glass-card p-3 lg:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-2 lg:mb-0">
                <p className="text-xs lg:text-sm text-white/60">Total Spent</p>
                <p className="text-lg lg:text-2xl font-bold text-rose-400 break-all">
                  ₹{totalSpent.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="bg-rose-500/20 p-2 rounded-full">
                <Target className="h-5 w-5 lg:h-6 lg:w-6 text-rose-400 self-end lg:self-auto" />
              </div>
            </div>
          </div>

          {/* Active Categories */}
          <div className="glass-card p-3 lg:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-2 lg:mb-0">
                <p className="text-xs lg:text-sm text-white/60">Categories</p>
                <p className="text-lg lg:text-2xl font-bold text-white">
                  {categories.length}
                </p>
              </div>
              <div className="bg-amber-500/20 p-2 rounded-full">
                <PieChart className="h-5 w-5 lg:h-6 lg:w-6 text-amber-400 self-end lg:self-auto" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Payment Categories */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <div className="flex items-center space-x-2">
              <Send className="h-4 w-4 lg:h-5 lg:w-5 text-indigo-400" />
              <h2 className="text-lg lg:text-xl font-semibold text-white">
                Quick Payment
              </h2>
            </div>
            <button
              onClick={() => navigate('/budget-planning')}
              className="text-indigo-400 hover:text-indigo-300 text-xs lg:text-sm font-medium transition-colors"
            >
              Manage Categories
            </button>
          </div>

          <p className="text-white/60 mb-4 lg:mb-6 text-sm lg:text-base">
            Select a category to make instant UPI payments from your budget
          </p>

          {/* Categories Grid */}
          {availableCategories.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
              {availableCategories.map(category => {
                const balance = category.current_balance || category.balance || 0

                return (
                  <div
                    key={category.id}
                    onClick={() => handleCategoryPayment(category)}
                    className="glass-card hover:bg-white/10 transition-all duration-200 cursor-pointer group p-3 lg:p-4 min-h-[44px]"
                  >
                    <div className="flex items-start justify-between mb-2 lg:mb-3">
                      <div
                        className="w-3 h-3 lg:w-4 lg:h-4 rounded-full flex-shrink-0 mt-1"
                        style={{ backgroundColor: category.color || '#6B7280' }}
                      />
                      <div className="flex items-center space-x-1">
                        {category.hasQR && (
                          <div className="w-4 h-4 lg:w-5 lg:h-5 bg-white/10 rounded p-1">
                            <div className="w-full h-full bg-white rounded-sm opacity-60" />
                          </div>
                        )}
                        <Send className="h-3 w-3 lg:h-4 lg:w-4 text-white/40 group-hover:text-indigo-400 transition-colors" />
                      </div>
                    </div>

                    <h3 className="font-semibold text-white mb-2 group-hover:text-indigo-400 transition-colors text-sm lg:text-base break-words">
                      {category.name}
                    </h3>

                    <div className="space-y-1 lg:space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs lg:text-sm text-white/60">Available</span>
                        <span className="font-bold text-white text-xs lg:text-sm break-all">
                          ₹{balance.toLocaleString('en-IN')}
                        </span>
                      </div>

                      {category.percentage > 0 && (
                        <div className="text-xs text-white/40">
                          {category.percentage}% of salary
                        </div>
                      )}

                      {category.savedUpiId && (
                        <div className="text-xs text-indigo-300/80 truncate">
                          UPI: {category.savedUpiId}
                        </div>
                      )}
                    </div>

                    <div className="mt-2 lg:mt-3 pt-2 lg:pt-3 border-t border-white/10">
                      <div className="flex items-center justify-center text-xs lg:text-sm text-indigo-400 group-hover:text-indigo-300 transition-colors min-h-[20px]">
                        <Wallet className="h-3 w-3 lg:h-4 lg:w-4 mr-1" />
                        Pay Now
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="glass-card text-center py-8 lg:py-12">
              <Wallet className="h-12 w-12 lg:h-16 lg:w-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg lg:text-xl font-medium text-white mb-2">
                No payment categories
              </h3>
              <p className="text-white/60 mb-6 text-sm lg:text-base">
                Create budget categories to start making payments
              </p>
              <button
                onClick={() => navigate('/budget-planning')}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Categories
              </button>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Recent Transactions */}
          <div className="glass-card p-4 lg:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base lg:text-lg font-semibold text-white">
                Recent Transactions
              </h2>
              <button
                onClick={() => navigate('/transactions')}
                className="text-indigo-400 hover:text-indigo-300 text-xs lg:text-sm font-medium transition-colors"
              >
                View All
              </button>
            </div>

            {recentTransactions.length > 0 ? (
              <div className="space-y-3">
                {recentTransactions.map(transaction => (
                  <div key={transaction.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: transaction.category_color || '#6B7280' }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-white text-sm truncate">
                          {transaction.merchant_name || 'Unknown Merchant'}
                        </p>
                        <p className="text-xs text-white/50 truncate">
                          {transaction.category_name} • {new Date(transaction.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="font-medium text-white text-sm">
                        -₹{transaction.amount.toLocaleString('en-IN')}
                      </p>
                      <p className={`text-xs ${transaction.status === 'success' ? 'text-emerald-400' :
                        transaction.status === 'failed' ? 'text-rose-400' :
                          'text-amber-400'
                        }`}>
                        {transaction.status}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-white/40">
                <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No transactions yet</p>
              </div>
            )}
          </div>

          {/* Top Categories */}
          <div className="glass-card p-4 lg:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base lg:text-lg font-semibold text-white">
                Top Categories
              </h2>
              <button
                onClick={() => navigate('/budget-planning')}
                className="text-indigo-400 hover:text-indigo-300 text-xs lg:text-sm font-medium transition-colors"
              >
                View All
              </button>
            </div>

            {topCategories.length > 0 ? (
              <div className="space-y-4">
                {topCategories.map(category => (
                  <div key={category.id} className="flex items-center justify-between border-b border-white/5 last:border-0 pb-3 last:pb-0">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: category.color || '#6B7280' }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-white text-sm truncate">
                          {category.name}
                        </p>
                        <p className="text-xs text-white/50 truncate">
                          {category.percentage > 0
                            ? `${category.percentage}% of salary`
                            : `₹${category.fixed_amount || 0} fixed`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="font-semibold text-white text-sm">
                        ₹{(category.current_balance || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-white/40">
                <PieChart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No categories yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Payment Modal */}
        <QuickPayment
          isOpen={showQuickPayment}
          onClose={() => {
            setShowQuickPayment(false)
            setSelectedCategory(null)
          }}
          preSelectedCategory={selectedCategory}
        />
      </div>
    </div>
  )
}

export default Home